import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import StepCard from 'shared/components/StepCard';
import FormRadioGroup from 'shared/components/forms/FormRadioGroup';
import FormSelect from 'shared/components/forms/FormSelect';
import FormInput from 'shared/components/forms/FormInput';
import FormAutocomplete from 'shared/components/forms/FormAutocomplete';
import FileUpload from 'features/onboarding/components/upload/FileUpload';
import StepDocumentSection from 'features/onboarding/components/upload/StepDocumentSection';
import { useOnboarding } from 'features/onboarding/context/OnboardingContext';
import { useRegisterStepNav } from 'features/onboarding/context/StepNavContext';
import { usePosSystems } from 'features/onboarding/hooks/usePosSystems';
import { stepMenuSchema } from 'features/onboarding/schemas';
import {
  MENU_SOURCES,
  type FileMeta,
  type MenuSource,
} from 'features/onboarding/types/onboarding.types';
import { getRuntimeEnv } from 'shared/lib/runtimeConfig';

const MENU_ACCEPT: Record<string, string[]> = {
  'application/pdf': ['.pdf'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.ms-excel': ['.xls'],
  'text/csv': ['.csv'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
};

const IMAGE_ONLY_ACCEPT: Record<string, string[]> = {
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
};

// Sentinel value for the "other" POS option so the free-text input can render.
const POS_OTHER = '__other__';

interface LocalState {
  menuSource: MenuSource | null;
  menuWebsiteUrl: string;
  menuFile?: FileMeta;
  posType: string;
  wantsMenuPhotoShoot: boolean | null;
}

/**
 * Final step before review — how the partner provides their menu, the optional
 * menu photo-shoot request, and the (optional) business logo.
 */
const Step10FinalDetails: React.FC = () => {
  const { t } = useTranslation();
  const { data, updateSection, goNext, goBack, registerStepGuard } = useOnboarding();

  const [local, setLocal] = useState<LocalState>({
    menuSource: data.businessQuestions.menuSource,
    menuWebsiteUrl: data.businessQuestions.menuWebsiteUrl ?? '',
    menuFile: data.businessQuestions.menuFile,
    posType: data.businessQuestions.posType ?? '',
    wantsMenuPhotoShoot: data.businessQuestions.wantsMenuPhotoShoot,
  });
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});

  const { data: posSystems, isLoading: posLoading } = usePosSystems(
    local.menuSource === 'pos',
  );

  const [isPosOther, setIsPosOther] = useState<boolean>(() =>
    Boolean(data.businessQuestions.posType?.trim()),
  );

  const posOptions = useMemo(
    () => [
      ...posSystems.map((p) => ({ value: p.label, label: p.label })),
      { value: POS_OTHER, label: t('businessQuestions.menuSource.posOtherOption') as string },
    ],
    [posSystems, t],
  );

  useEffect(() => {
    const current = local.posType?.trim();
    if (!current) return;
    if (posSystems.some((p) => p.label === current)) setIsPosOther(false);
  }, [posSystems, local.posType]);

  const yesNo = [
    { value: true, label: t('common.yes') as string },
    { value: false, label: t('common.no') as string },
  ];

  const validate = (): boolean => {
    const result = stepMenuSchema.safeParse(local);
    if (result.success) {
      setErrors({});
      return true;
    }
    const next: Record<string, string | undefined> = {};
    result.error.issues.forEach((issue) => {
      const key = issue.path[0];
      if (typeof key === 'string' && !next[key]) next[key] = issue.message;
    });
    setErrors(next);
    return false;
  };

  const persist = () => {
    updateSection('businessQuestions', {
      menuSource: local.menuSource,
      menuWebsiteUrl: local.menuWebsiteUrl,
      menuFile: local.menuFile,
      posType: local.posType,
      wantsMenuPhotoShoot: local.wantsMenuPhotoShoot,
    });
  };

  const handleNext = () => {
    if (!validate()) return;
    persist();
    goNext();
  };

  useRegisterStepNav({
    onBack: goBack,
    onNext: handleNext,
    nextLabel: t('nav.visuals') as string,
  });

  const validateRef = useRef(validate);
  validateRef.current = validate;
  const persistRef = useRef(persist);
  persistRef.current = persist;

  useEffect(() => {
    registerStepGuard(() => {
      if (!validateRef.current()) return false;
      persistRef.current();
      return true;
    });
    return () => registerStepGuard(null);
  }, [registerStepGuard]);

  return (
    <StepCard title={t('menu.title')} subtitle={t('menu.subtitle') as string}>
      <div className="flex flex-col gap-6">
        <FormSelect
          name="menuSource"
          label={t('businessQuestions.menuSource.question') as string}
          placeholder={t('businessQuestions.menuSource.placeholder') as string}
          options={MENU_SOURCES.map((src) => ({
            value: src,
            label: t(`businessQuestions.menuSource.options.${src}`) as string,
          }))}
          value={local.menuSource ?? ''}
          onChange={(e) => {
            const v = e.target.value as MenuSource;
            if (v !== 'pos') setIsPosOther(false);
            setLocal((s) => ({
              ...s,
              menuSource: v,
              menuFile: v === 'upload' || v === 'pos' ? s.menuFile : undefined,
              menuWebsiteUrl: v === 'website' ? s.menuWebsiteUrl : '',
              posType: v === 'pos' ? s.posType : '',
            }));
          }}
          error={errors.menuSource}
          required
        />

        {local.menuSource === 'upload' && (
          <FileUpload
            label={t('businessQuestions.menuFileUploadLabel') as string}
            value={local.menuFile}
            accept={MENU_ACCEPT}
            required
            onChange={(meta: FileMeta | null) =>
              setLocal((s) => ({ ...s, menuFile: meta ?? undefined }))
            }
            error={errors.menuFile}
            hint={t('businessQuestions.menuAccepted') as string}
          />
        )}

        {local.menuSource === 'website' && (
          <FormInput
            label={t('businessQuestions.menuSource.websiteLabel') as string}
            placeholder={t('businessQuestions.menuSource.websitePlaceholder') as string}
            type="url"
            inputMode="url"
            value={local.menuWebsiteUrl}
            onChange={(e) => setLocal((s) => ({ ...s, menuWebsiteUrl: e.target.value }))}
            error={errors.menuWebsiteUrl}
            required
          />
        )}

        {local.menuSource === 'pos' && (
          <>
            <FormAutocomplete
              label={t('businessQuestions.menuSource.posSelectLabel') as string}
              placeholder={t('businessQuestions.menuSource.posPlaceholder') as string}
              options={posOptions}
              disabled={posLoading}
              value={isPosOther ? POS_OTHER : local.posType}
              onChange={(v) => {
                if (v === POS_OTHER) {
                  setIsPosOther(true);
                  setLocal((s) => ({ ...s, posType: '' }));
                } else {
                  setIsPosOther(false);
                  setLocal((s) => ({ ...s, posType: v }));
                }
              }}
              error={isPosOther ? undefined : errors.posType}
              required
            />
            {isPosOther && (
              <FormInput
                label={t('businessQuestions.menuSource.posOtherLabel') as string}
                placeholder={t('businessQuestions.menuSource.posOtherPlaceholder') as string}
                value={local.posType}
                onChange={(e) => setLocal((s) => ({ ...s, posType: e.target.value }))}
                error={errors.posType}
                required
              />
            )}
            {local.posType.trim() && (
              <FileUpload
                label={t('businessQuestions.menuFileUploadLabel') as string}
                value={local.menuFile}
                accept={MENU_ACCEPT}
                onChange={(meta: FileMeta | null) =>
                  setLocal((s) => ({ ...s, menuFile: meta ?? undefined }))
                }
                hint={t('businessQuestions.menuAccepted') as string}
              />
            )}
          </>
        )}

        <FormRadioGroup<boolean>
          name="wantsMenuPhotoShoot"
          label={t('businessQuestions.wantsMenuPhotoShoot') as string}
          options={yesNo}
          value={local.wantsMenuPhotoShoot}
          onChange={(v) => setLocal((s) => ({ ...s, wantsMenuPhotoShoot: v }))}
          error={errors.wantsMenuPhotoShoot}
          required
        />

        {local.wantsMenuPhotoShoot === true &&
          Boolean(getRuntimeEnv('REACT_APP_MENU_PHOTOSHOOT_SCHEDULING_URL')) && (
            <a
              href={String(
                getRuntimeEnv('REACT_APP_MENU_PHOTOSHOOT_SCHEDULING_URL'),
              )}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center rounded-xl px-4 py-3 text-sm font-semibold text-white"
              style={{ backgroundColor: '#c00035' }}
            >
              {t('businessQuestions.menuPhotoShootSchedule') as string}
            </a>
          )}

        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-gray-700">
            {t('menu.logoSectionTitle')}
          </span>
          <StepDocumentSection docKey="businessLogo" accept={IMAGE_ONLY_ACCEPT} />
        </div>
      </div>

    </StepCard>
  );
};

export default Step10FinalDetails;
